import { Link } from 'react-router-dom'
import type { ReactNode, MouseEvent } from 'react'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'

interface GuardedLinkProps {
  to: string
  className?: string
  title?: string
  children: ReactNode
  /** Side effect that belongs to the navigation, run only once it is confirmed. */
  onNavigate?: () => void
}

/**
 * A <Link> that stops to ask when the note editor holds unsaved text.
 *
 * Still a real anchor, so middle-click, ctrl-click and "copy link address" behave normally — those
 * open a new context rather than discarding this one, and are deliberately left alone. Only the
 * plain left-click, which replaces the current view, is routed through the guard.
 */
export function GuardedLink({ to, className, title, children, onNavigate }: GuardedLinkProps) {
  const navigate = useGuardedNavigate()

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    onNavigate?.()
    navigate(to)
  }

  return (
    <Link to={to} className={className} title={title} onClick={handleClick}>
      {children}
    </Link>
  )
}
